
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { ToastService } from './toast.service';


@Injectable()
export class FileService {

    constructor(private toast: ToastService) { }

    downloadDocument(documentResponse) {
        console.info('Downloading document...');
        const docBody = documentResponse;
        const fileContent = docBody?.Content || docBody?.Content2;

        if (!this.isValidFileContent(fileContent)) {
            const apiMessage = this.extractApiMessage(docBody);
            console.warn('File content is not available or invalid. ' + (apiMessage ? ('API message: ' + apiMessage) : ''));
            this.toast.warn(apiMessage || 'The requested file is not available.', 'File Not Available');
            return;
        }

        const fileName = docBody.Extension != null && docBody.FileName.indexOf(docBody.Extension) < 0 ? (docBody.FileName + '.' + docBody.Extension) : docBody.FileName;
        const blob = this.base64toBlob(fileContent, docBody.MIMEType);

        if (!blob || blob.size === 0) {
            console.warn('Could not create a valid file from the response.');
            this.toast.warn('The requested file is not available.', 'File Not Available');
            return;
        }

        console.info('Created blob from ' + fileName);
        try {
            saveAs(blob, fileName);
        }
        catch (err) {
            console.error('Error downloading ' + fileName + ': ' + JSON.stringify(err));
            window.navigator.msSaveBlob(blob, fileName);
        }
    }

    private isValidFileContent(content): boolean {
        return typeof content === 'string' && content.trim().length > 0;
    }

    /**
     * Best-effort extraction of a human readable message from an arbitrary
     * (non-file) JSON response body returned by the API. Different endpoints
     * may use different casing/key names, so a list of common candidates is checked.
     */
    private extractApiMessage(body: any): string | undefined {
        if (body == null) {
            return undefined;
        }

        if (typeof body === 'string') {
            return body.trim().length > 0 ? body : undefined;
        }

        if (typeof body !== 'object') {
            return undefined;
        }

        const candidateKeys = ['message', 'Message', 'error', 'Error', 'errorMessage', 'ErrorMessage', 'msg', 'Msg', 'detail', 'Detail', 'title', 'Title'];
        for (const key of candidateKeys) {
            const value = body[key];
            if (typeof value === 'string' && value.trim().length > 0) {
                return value;
            }
        }

        return undefined;
    }

    private base64toBlob(base64Data, contentType = '', sliceSize = 512) {
        console.info('Decoding base 64 data...');
        try {
            const byteCharacters = window.atob(base64Data.replace(/[\n\r]/g, ''));

            console.info('Decoded base 64 data');
            const byteArrays = [];

            console.info('Decoding data (' + byteCharacters.length + ' bytes)...')
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                console.debug('Decoding slice ' + (offset + 1) + '...');
                const slice = byteCharacters.slice(offset, offset + sliceSize);

                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);

                byteArrays.push(byteArray);
            }

            console.info('Creating blob...');
            const blob = new Blob(byteArrays, { type: contentType });
            return blob;
        } catch (error) {
            console.error('Could not decode base 64 data: ' + error);
        }
    }

    public downloadPdfDocument(documentResponse, filename, format) {
        const responseBody: Blob = documentResponse?.body;
        const fileName = filename + '.' + format;

        if (!responseBody || responseBody.size === 0) {
            console.warn('PDF response body is empty or missing.');
            this.toast.warn('The requested file is not available.', 'File Not Available');
            return;
        }

        const contentType = (documentResponse?.headers?.get?.('content-type') || responseBody.type || '').toLowerCase();

        // API returned a JSON error payload instead of the actual PDF bytes.
        if (contentType.indexOf('json') > -1) {
            responseBody.text().then(text => {
                let apiMessage: string | undefined;
                try {
                    apiMessage = this.extractApiMessage(JSON.parse(text));
                } catch (parseErr) {
                    console.error('Could not parse JSON error response: ' + parseErr);
                }
                console.warn('Received JSON response instead of a PDF file. ' + (apiMessage ? ('API message: ' + apiMessage) : ''));
                this.toast.warn(apiMessage || 'The requested file is not available.', 'File Not Available');
            });
            return;
        }

        const blob = new Blob([responseBody], { type: 'application/pdf' });
        console.info('Created blob from ' + fileName);
        try {
            saveAs(blob, fileName);
        }
        catch (err) {
            console.error('Error downloading ' + fileName + ': ' + JSON.stringify(err));
            window.navigator.msSaveBlob(blob, fileName);
        }
    }
}